import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="testimonial-carousel"
export default class extends Controller {
  static targets = ["slide", "indicator"]
  static values = {
    autoAdvanceInterval: { type: Number, default: 5000 },
    currentIndex: { type: Number, default: 0 }
  }

  connect() {
    console.log("🎠 Testimonial carousel controller connected")
    this.totalSlides = this.slideTargets.length
    this.isPaused = false
    this.startAutoAdvance()
    this.setupTouchHandling()

    // Initialize first slide as active
    this.showSlide(this.currentIndexValue)
    this.updateIndicators()
  }

  disconnect() {
    console.log("🎠 Testimonial carousel controller disconnected")
    this.stopAutoAdvance()
  }

  // Action methods
  next() {
    console.log("➡️ Next slide requested")
    this.goToSlide((this.currentIndexValue + 1) % this.totalSlides)
  }

  previous() {
    console.log("⬅️ Previous slide requested")
    this.goToSlide((this.currentIndexValue - 1 + this.totalSlides) % this.totalSlides)
  }

  goToSlide(index) {
    console.log(`🎯 Going to slide ${index}`)
    this.currentIndexValue = index
    this.showSlide(index)
    this.updateIndicators()
    this.resetAutoAdvance()
  }

  // Touch/swipe handling
  setupTouchHandling() {
    let startX = 0
    let startY = 0
    let threshold = 50 // minimum distance for swipe

    this.element.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX
      startY = e.touches[0].clientY
    })

    this.element.addEventListener('touchend', (e) => {
      if (!startX || !startY) return

      let endX = e.changedTouches[0].clientX
      let endY = e.changedTouches[0].clientY

      let diffX = startX - endX
      let diffY = startY - endY

      // Only process horizontal swipes (ignore vertical)
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          this.next() // Swipe left - go to next
        } else {
          this.previous() // Swipe right - go to previous
        }
      }

      startX = 0
      startY = 0
    })
  }

  // Auto-advance functionality
  startAutoAdvance() {
    console.log("▶️ Starting auto-advance")
    this.autoAdvanceTimer = setInterval(() => {
      if (!this.isPaused) {
        this.next()
      }
    }, this.autoAdvanceIntervalValue)
  }

  stopAutoAdvance() {
    console.log("⏹️ Stopping auto-advance")
    if (this.autoAdvanceTimer) {
      clearInterval(this.autoAdvanceTimer)
      this.autoAdvanceTimer = null
    }
  }

  resetAutoAdvance() {
    this.stopAutoAdvance()
    this.startAutoAdvance()
  }

  // Pause/resume on hover
  pause() {
    console.log("⏸️ Pausing auto-advance")
    this.isPaused = true
  }

  resume() {
    console.log("▶️ Resuming auto-advance")
    this.isPaused = false
  }

  // Display methods
  showSlide(index) {
    // Hide all slides
    this.slideTargets.forEach((slide, i) => {
      slide.classList.remove('active')
      slide.style.opacity = '0'
      slide.style.transform = 'translateX(20px)'
      slide.setAttribute('aria-hidden', 'true')
    })

    // Show current slide with animation
    const currentSlide = this.slideTargets[index]
    if (currentSlide) {
      // Use setTimeout to ensure smooth transition
      setTimeout(() => {
        currentSlide.classList.add('active')
        currentSlide.style.opacity = '1'
        currentSlide.style.transform = 'translateX(0)'
        currentSlide.setAttribute('aria-hidden', 'false')
      }, 50)
    }
  }

  updateIndicators() {
    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((indicator, i) => {
        indicator.classList.toggle('active', i === this.currentIndexValue)
        indicator.setAttribute('aria-current', i === this.currentIndexValue ? 'true' : 'false')
      })
    }
  }

  // Indicator click handler
  selectSlide(event) {
    const index = parseInt(event.currentTarget.dataset.slideIndex)
    console.log(`🎯 Indicator clicked for slide ${index}`)
    this.goToSlide(index)
  }
}